import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ListRenderItem } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedScrollHandler,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { SNAPPING_INTERVAL_FAKE_ITEM } from './ParallaxList.content';
import {
  ParallaxListContainerProps,
  ParallaxListProps,
} from './ParallaxList.types';
import useLayout from '../../hooks/useLayout';
import ParallaxListItem from './ParallaxListItem/ParallaxListItem';
import Box from '../Box/Box';
import Pagination from '../Pagination/Pagination';
import { useWebDragScroll } from './hooks/useWebDragScroll';
import { useDebouncedCallback } from '@bonanza-social/toolkit/hooks';

function ParallaxList({
  data,
  activeIndex,
  containerWidth,
  renderItem,
  onChangeActiveIndex,
}: ParallaxListProps) {
  const itemWidth = containerWidth / 3;
  const scrollX = useSharedValue(data.length > 2 ? itemWidth * 2 : itemWidth);
  const listRef = useRef<Animated.FlatList<unknown>>(null);
  const modifiedData = useMemo(
    () => [SNAPPING_INTERVAL_FAKE_ITEM, ...data],
    [data],
  );
  const [disableSnapping, setDisableSnapping] = useState(false);

  const onScroll = (x: number) => {
    'worklet';

    const index = Math.round(Math.max(0, x - itemWidth) / itemWidth);
    const nextActiveIndex = Math.min(Math.max(0, index), data.length - 1);

    runOnJS(onChangeActiveIndex)(nextActiveIndex);
  };

  const onScrollEnd = useDebouncedCallback(
    useCallback(() => {
      setDisableSnapping(false);

      if (scrollX.value < itemWidth) {
        listRef.current?.scrollToOffset({ offset: itemWidth });
      }
    }, [itemWidth, scrollX]),
    100,
  );

  const handleScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollX.value = event.contentOffset.x;

      onScroll(event.contentOffset.x);

      runOnJS(onScrollEnd)();
    },
  });

  // Handle web mouse scroll
  const { dragging, handleMouseDown } = useWebDragScroll({
    listRef,
    scrollX,
    itemWidth,
    dataLength: data.length,
  });

  const options = useMemo(
    () => ({
      style: { scrollSnapType: dragging || disableSnapping ? 'none' : 'x' },
      contentContainerStyle: { paddingHorizontal: itemWidth },
    }),
    [itemWidth, dragging, disableSnapping],
  );

  const _renderItem = useCallback<ListRenderItem<unknown>>(
    ({ item, index }) => (
      <ParallaxListItem
        index={index}
        itemWidth={itemWidth}
        scrollX={scrollX}
        scrollSnapAlign={dragging || disableSnapping ? 'none' : 'start'}
      >
        {item !== SNAPPING_INTERVAL_FAKE_ITEM && renderItem(item)}
      </ParallaxListItem>
    ),
    [itemWidth, scrollX, dragging, disableSnapping, renderItem],
  );

  const handlePaginationPress = useCallback(
    (index: number) => {
      setDisableSnapping(true);
      setTimeout(() => {
        listRef.current?.scrollToOffset({
          offset: index * itemWidth + itemWidth - 0.5,
        });
      }, 0);
    },
    [itemWidth],
  );

  useEffect(() => {
    listRef.current?.scrollToOffset({
      offset: data.length > 2 ? itemWidth * 2 : itemWidth,
      animated: false,
    });
  }, [data.length, itemWidth]);

  return (
    <>
      <Animated.FlatList
        ref={listRef}
        data={modifiedData}
        decelerationRate="fast"
        showsHorizontalScrollIndicator={false}
        disableIntervalMomentum
        scrollEventThrottle={16}
        renderItem={_renderItem}
        onScroll={handleScroll}
        horizontal
        {...options}
        onMouseDown={handleMouseDown}
      />
      {data.length > 1 && (
        <Pagination
          data={data}
          activeIndex={activeIndex}
          onPress={handlePaginationPress}
          marginTop="space-400"
        />
      )}
    </>
  );
}

function ParallaxListContainer({
  data,
  activeIndex,
  renderItem,
  onChangeActiveIndex,
  ...rest
}: ParallaxListContainerProps) {
  const [layout, onLayout] = useLayout();

  return (
    <Box onLayout={onLayout} {...rest}>
      {layout.width > 0 && (
        <ParallaxList
          data={data}
          activeIndex={activeIndex}
          containerWidth={layout.width}
          renderItem={renderItem}
          onChangeActiveIndex={onChangeActiveIndex}
        />
      )}
    </Box>
  );
}

export default memo(ParallaxListContainer);
